import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

type Tone = 'neutral' | 'success' | 'warn' | 'danger';

interface ToastItem {
  id: number;
  message: string;
  tone: Tone;
  ttl: number;
}

interface ToastApi {
  show: (message: string, opts?: { tone?: Tone; ttlMs?: number }) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(1);

  const show = useCallback(
    (message: string, opts?: { tone?: Tone; ttlMs?: number }) => {
      const id = seq.current++;
      const item: ToastItem = {
        id,
        message,
        tone: opts?.tone ?? 'neutral',
        ttl: opts?.ttlMs ?? 3500,
      };
      setItems((prev) => [...prev, item]);
      window.setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id));
      }, item.ttl);
    },
    [],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed inset-x-0 top-3 z-50 mx-auto flex max-w-app flex-col items-center gap-2 px-4"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'pointer-events-auto pb-fade-up rounded-pb-md border px-4 py-2.5 text-[13px] font-medium shadow-pb-md',
              t.tone === 'success' &&
                'border-pb-success bg-pb-success-soft text-pb-success',
              t.tone === 'warn' &&
                'border-pb-warn/40 bg-[color-mix(in_oklab,var(--pb-warn)_18%,transparent)] text-pb-warn',
              t.tone === 'danger' &&
                'border-pb-danger/40 bg-[color-mix(in_oklab,var(--pb-danger)_18%,transparent)] text-pb-danger',
              t.tone === 'neutral' &&
                'border-pb-border bg-pb-paper text-tg-text',
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
